// ABOUTME: Admin child management component with searchable, sortable, paginated data table
// ABOUTME: Provides child listing, filtering, navigation to detail views, matching AdminUserManagement functionality

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  TextField,
  CircularProgress,
  Alert,
  Container,
  Button,
  Chip
} from '@mui/material';
import {
  ArrowUpward,
  ArrowDownward,
  ArrowBack
} from '@mui/icons-material';
import {
  useReactTable,
  getCoreRowModel,
  createColumnHelper,
  flexRender,
  SortingState,
  ColumnFiltersState,
  getPaginationRowModel,
  Column
} from '@tanstack/react-table';
import { useTheme } from '@mui/material/styles';
import * as api from '../services/api';
import { formatRelativeTime } from '../utils/timeUtils';
import SoftDeleteToggle, { SoftDeleteFilter } from './SoftDeleteToggle';

interface ChildListItem {
  id: number;
  childId: number;
  name: string;
  age: number;
  familyId: number;
  familyName: string;
  lastBehaviorRecord: string | null;
  lastBehaviorEventTime?: string | null;
  lastBehaviorEventLocalTime?: string | null;
  primaryParentNames: string;
  createdAt: string;
  isActive: boolean;
  isDeleted: boolean;
}

interface PaginatedChildren {
  children: ChildListItem[];
  totalCount: number;
  totalPages: number;
}

const AdminChildManagement: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  // Get the time for relative calculations (use actual UTC moment for "X hours ago")
  const getLastBehaviorDisplayTime = (child: ChildListItem): string | null => {
    // For admin lists showing "X hours ago", use EventTime (actual UTC moment) for accurate elapsed time
    // Don't use EventLocalTime here as it represents display time, not the actual moment
    return child.lastBehaviorEventTime || child.lastBehaviorRecord;
  };
  const [data, setData] = useState<ChildListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 25,
  });
  const [softDeleteFilter, setSoftDeleteFilter] = useState<SoftDeleteFilter>('active');

  const columnHelper = createColumnHelper<ChildListItem>();

  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        header: 'Name',
        cell: (info) => {
          const child = info.row.original;
          const isDeleted = child.isDeleted;
          const isGreyedOut = softDeleteFilter === 'all' && isDeleted;
          
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                component="span"
                sx={{
                  color: isGreyedOut ? theme.palette.grey[500] : theme.palette.info.main,
                  fontWeight: 600,
                  textDecoration: isDeleted && softDeleteFilter === 'all' ? 'line-through' : 'none'
                }}
              >
                {info.getValue()}
              </Box>
              {isDeleted && (
                <Chip 
                  label="DELETED" 
                  size="small" 
                  color="error" 
                  variant="outlined"
                  sx={{ fontSize: '0.7rem', height: '20px' }}
                />
              )}
            </Box>
          );
        },
        meta: {
          filterPlaceholder: 'Search name...'
        }
      }),
      columnHelper.accessor('age', {
        header: 'Age',
        cell: (info) => info.getValue(),
        enableColumnFilter: false
      }),
      columnHelper.accessor('familyName', {
        header: 'Family Name',
        cell: (info) => info.getValue(),
        meta: {
          filterPlaceholder: 'Search family name...'
        }
      }),
      columnHelper.accessor((row) => row.lastBehaviorRecord || '', {
        id: 'lastBehaviorRecord',
        header: 'Last Recorded Behavior',
        cell: (info) => {
          const child = info.row.original;
          const displayTime = getLastBehaviorDisplayTime(child);
          if (!displayTime) return 'No activity';
          return <span>{formatRelativeTime(displayTime)}</span>;
        },
        enableColumnFilter: false
      }),
      columnHelper.accessor('primaryParentNames', {
        header: 'Family Adults',
        cell: (info) => {
          const value = info.getValue();
          return value === 'Not Available' ? 'No family members' : value;
        },
        enableColumnFilter: false,
        enableSorting: false
      }),
    ],
    [columnHelper, softDeleteFilter, theme]
  );

  const fetchChildren = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const searchParams: Parameters<typeof api.getAllChildren>[0] = {
        page: pagination.pageIndex + 1,
        pageSize: pagination.pageSize
      };

      // Add soft delete filter
      if (softDeleteFilter === 'deleted') {
        searchParams.showDeleted = 'deleted';
      } else if (softDeleteFilter === 'all') {
        searchParams.showDeleted = 'all';
      }
      // 'active' is the default, no parameter needed

      // Add search filters - combine all filters into searchTerm for backend
      const searchTerms: string[] = [];
      columnFilters.forEach(filter => {
        if (filter.value && typeof filter.value === 'string') {
          searchTerms.push(filter.value);
        }
      });
      
      if (searchTerms.length > 0) {
        searchParams.searchTerm = searchTerms.join(' ');
      }

      // Add sorting
      if (sorting.length > 0) {
        const sort = sorting[0];
        // Map frontend column names to backend expected names
        const sortByMapping: Record<string, string> = {
          'lastBehaviorRecord': 'lastactivity'
        };
        searchParams.sortBy = sortByMapping[sort.id] || sort.id;
        searchParams.sortOrder = sort.desc ? 'desc' : 'asc';
      }

      const result = await api.getAllChildren(searchParams) as PaginatedChildren;
      setData(result.children);
      setTotalCount(result.totalCount);
    } catch (err: unknown) {
      setError('Error loading children. Please try again.');
      console.error('Error fetching children:', err);
    } finally {
      setLoading(false);
    }
  }, [pagination, sorting, columnFilters, softDeleteFilter]);

  useEffect(() => {
    fetchChildren();
  }, [fetchChildren]);

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      columnFilters,
      pagination,
    },
    pageCount: Math.ceil(totalCount / pagination.pageSize),
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
  });

  const handleNavigateBack = () => {
    navigate('/admin');
  };

  const handleRowClick = (childId: number) => {
    navigate(`/admin/children/${childId}`);
  };

  const getSortIcon = (column: Column<ChildListItem, unknown>) => {
    const sortState = column.getIsSorted();
    if (sortState === 'asc') {
      return <ArrowUpward fontSize="small" />;
    } else if (sortState === 'desc') {
      return <ArrowDownward fontSize="small" />;
    }
    return null;
  };

  if (loading && data.length === 0) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Loading children...</Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBack />}
          onClick={handleNavigateBack}
          sx={{ mr: 2 }}
        >
          Back to Admin
        </Button>
        <Box>
          <Typography variant="h3" component="h1" sx={{ fontWeight: 600, mb: 1 }}>
            Child Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage all children across families
          </Typography>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 4 }}>
          {error}
          <Button onClick={fetchChildren} sx={{ ml: 2 }}>
            Retry
          </Button>
        </Alert>
      )}

      {/* Data Table */}
      <Paper sx={{ p: 3 }}>
        <Box data-testid="child-table-container" sx={{ position: 'relative' }}>
          {/* Soft Delete Filter Toggle */}
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" component="h2">
              Children ({totalCount})
            </Typography>
            <SoftDeleteToggle
              value={softDeleteFilter}
              onChange={(newFilter) => {
                setSoftDeleteFilter(newFilter);
                setPagination(prev => ({ ...prev, pageIndex: 0 })); // Reset to first page
              }}
              disabled={loading}
            />
          </Box>

          {/* Search Filters */}
          <Box sx={{ display: 'grid', gridTemplateColumns: '25% 10% 20% 20% 25%', gap: 2, mb: 3 }}>
            {table.getHeaderGroups()[0]?.headers.map((header) => {
              return (
                <Box key={header.id}>
                  {header.column.getCanFilter() ? (
                    <TextField
                      size="small"
                      placeholder={(header.column.columnDef.meta as { filterPlaceholder?: string } | undefined)?.filterPlaceholder || 'Search...'}
                      value={(header.column.getFilterValue() as string) ?? ''}
                      onChange={(e) => header.column.setFilterValue(e.target.value)}
                      fullWidth
                    />
                  ) : null}
                </Box>
              );
            })}
          </Box>

          {/* Table */}
          <Box 
            data-testid="child-table"
            sx={{ 
              overflowX: 'auto',
              '& table': {
                width: '100%',
                borderCollapse: 'collapse',
                tableLayout: 'fixed'
              },
              '& th': {
                position: 'relative',
                minWidth: '120px'
              },
              '& th:nth-of-type(1)': { width: '25%' },
              '& th:nth-of-type(2)': { width: '10%' },
              '& th:nth-of-type(3)': { width: '20%' },
              '& th:nth-of-type(4)': { width: '20%' },
              '& th:nth-of-type(5)': { width: '25%' }
            }}
          >
            <Box component="table">
              <Box component="thead" data-testid="table-header" sx={{ position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 1 }}>
                {table.getHeaderGroups().map((headerGroup) => (
                  <Box component="tr" key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <Box
                        component="th"
                        key={header.id}
                        sx={{
                          padding: '12px',
                          textAlign: 'left',
                          borderBottom: `2px solid ${theme.palette.grey[300]}`,
                          cursor: header.column.getCanSort() ? 'pointer' : 'default'
                        }}
                        onClick={header.column.getToggleSortingHandler()}
                        data-testid={`sort-${header.id}`}
                      >
                        <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                          <span>{flexRender(header.column.columnDef.header, header.getContext())}</span>
                          <Box sx={{ width: '24px', height: '24px', display: 'flex', justifyContent: 'center', alignItems: 'center', flexShrink: 0 }}>
                            {getSortIcon(header.column)}
                          </Box>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                ))}
              </Box>
              <Box component="tbody">
                {!table || table.getRowModel().rows.length === 0 ? (
                  <Box component="tr">
                    <Box component="td" colSpan={columns.length} sx={{ padding: '24px', textAlign: 'center' }}>
                      <Typography color="text.secondary">
                        No children found
                      </Typography>
                    </Box>
                  </Box>
                ) : (
                  table.getRowModel().rows.map((row) => {
                    const isDeleted = row.original.isDeleted;
                    const isGreyedOut = softDeleteFilter === 'all' && isDeleted;

                    return (
                      <Box
                        component="tr"
                        key={row.id}
                        data-testid={`child-row-${row.original.id}`}
                        onClick={() => handleRowClick(row.original.id)}
                        sx={{
                          cursor: 'pointer',
                          borderBottom: `1px solid ${theme.palette.grey[300]}`,
                          opacity: isGreyedOut ? 0.5 : 1,
                          color: isGreyedOut ? theme.palette.grey[500] : 'inherit'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = theme.palette.grey[100];
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                      >
                      {row.getVisibleCells().map((cell) => (
                        <Box
                          component="td"
                          key={cell.id}
                          sx={{
                            padding: '12px',
                            color: isGreyedOut ? theme.palette.grey[500] : 'inherit'
                          }}
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </Box>
                      ))}
                    </Box>
                    );
                  })
                )}
              </Box>
            </Box>
          </Box>

          {/* Pagination */}
          <Box sx={{ 
            mt: 3, 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 2
          }}>
            {/* Pagination Info */}
            <Typography variant="body2" color="text.secondary">
              Showing {Math.min((pagination.pageIndex) * pagination.pageSize + 1, totalCount)} to {Math.min((pagination.pageIndex + 1) * pagination.pageSize, totalCount)} of {totalCount} children
            </Typography>
            
            {/* Pagination Controls */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Button
                size="small"
                variant="outlined"
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
              >
                Previous
              </Button>
              
              {/* Page Numbers */}
              {Array.from({ length: Math.ceil(totalCount / pagination.pageSize) }, (_, i) => i + 1)
                .slice(
                  Math.max(0, pagination.pageIndex - 2),
                  Math.min(Math.ceil(totalCount / pagination.pageSize), pagination.pageIndex + 3)
                )
                .map((page) => (
                  <Button
                    key={page}
                    size="small"
                    variant={page === pagination.pageIndex + 1 ? 'contained' : 'outlined'}
                    onClick={() => table.setPageIndex(page - 1)}
                    sx={{ minWidth: '32px', height: '32px' }}
                  >
                    {page}
                  </Button>
                ))}
              
              <Button
                size="small"
                variant="outlined"
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
              >
                Next
              </Button>
            </Box>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default AdminChildManagement;